import React from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';

export interface IRollCategories {
  submissions?: 'Got Submission' | 'Got Submitted' | 'Both Submitted' | 'None Submitted' | '';
  guard?: 'Passed Guard' | 'Guard Passed' | 'Both Passed' | 'None Passed' | '';
  mount?: 'Gained Mount' | 'Got Mounted' | 'Both Mounted' | 'None Got Mount' | '';
  back?: 'Gained Back' | 'Got Back Taken' | 'Both Gained Back' | 'None Got Back Taken' | '';
}

export interface IRollCategoriesSectionProps {
  rollCategories: IRollCategories;
  onCategoryChange: (category: keyof IRollCategories, value: string) => void;
  openCategories: string[];
  setOpenCategories: (categories: string[] | ((prev: string[]) => string[])) => void;
  showRollCategories?: boolean;
}

const ROLL_CATEGORIES = {
  submissions: {
    label: 'Submissions',
    values: [
      { key: 'Got Submission', isPositive: true },
      { key: 'Got Submitted', isNegative: true },
      { key: 'Both Submitted', isNeutral: true },
      { key: 'None Submitted', isNeutral: true },
    ],
  },
  guard: {
    label: 'Guard',
    values: [
      { key: 'Passed Guard', isPositive: true },
      { key: 'Guard Passed', isNegative: true },
      { key: 'Both Passed', isNeutral: true },
      { key: 'None Passed', isNeutral: true },
    ],
  },
  mount: {
    label: 'Mount',
    values: [
      { key: 'Gained Mount', isPositive: true },
      { key: 'Got Mounted', isNegative: true },
      { key: 'Both Mounted', isNeutral: true },
      { key: 'None Got Mount', isNeutral: true },
    ],
  },
  back: {
    label: 'Back',
    values: [
      { key: 'Gained Back', isPositive: true },
      { key: 'Got Back Taken', isNegative: true },
      { key: 'Both Gained Back', isNeutral: true },
      { key: 'None Got Back Taken', isNeutral: true },
    ],
  },
} as const;

export const RollCategoriesSection: React.FC<IRollCategoriesSectionProps> = ({
  rollCategories,
  onCategoryChange,
  openCategories,
  setOpenCategories,
  showRollCategories = false,
}) => {
  const { t } = useI18n();
  
  // Get all selected values for display when hidden
  const selectedValues = React.useMemo(() => {
    const values: Array<{ category: string; value: string; isPositive?: boolean; isNegative?: boolean; isNeutral?: boolean }> = [];
    
    // Debug: log rollCategories to see what we're working with
    // console.log('RollCategoriesSection - rollCategories:', rollCategories);
    
    (Object.keys(ROLL_CATEGORIES) as Array<keyof typeof ROLL_CATEGORIES>).forEach((categoryKey) => {
      const selectedValue = rollCategories[categoryKey];
      // Check if value exists, is a string, and is not empty
      if (selectedValue && typeof selectedValue === 'string' && selectedValue.trim() !== '') {
        const category = ROLL_CATEGORIES[categoryKey];
        const valueData = category.values.find(v => v.key === selectedValue);
        if (valueData) {
          values.push({
            category: categoryKey as string,
            value: selectedValue,
            isPositive: valueData.isPositive,
            isNegative: valueData.isNegative,
            isNeutral: valueData.isNeutral,
          });
        }
      }
    });
    
    // Debug: log selectedValues to see what we found
    // console.log('RollCategoriesSection - selectedValues:', selectedValues);
    
    return values;
  }, [rollCategories]);

  const toggleCategory = (categoryName: string): void => {
    setOpenCategories(prev => {
      if (prev.includes(categoryName)) {
        return prev.filter(cat => cat !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const isCategoryOpen = (categoryName: string): boolean => {
    return openCategories.includes(categoryName);
  };

  const handleValueSelect = (category: keyof IRollCategories, value: string): void => {
    // If clicking the same value, deselect it
    const currentValue = rollCategories[category];
    if (currentValue === value) {
      onCategoryChange(category, '');
    } else {
      onCategoryChange(category, value);
    }
  };

  // Render badges - always show when there are selected values
  const renderBadges = () => {
    if (selectedValues.length === 0) {
      return null;
    }
    
    return (
      <div className="flex flex-wrap gap-1 mb-3">
        {selectedValues.map((item) => {
          const colorClass = item.isPositive
            ? 'bg-green-500 text-white'
            : item.isNegative
            ? 'bg-red-500 text-white'
            : 'bg-black text-white';
          
          return (
            <span
              key={`${item.category}-${item.value}`}
              className={`px-2 py-1 text-xs font-medium rounded ${colorClass}`}
            >
              {t(`calendar.roll_value.${item.value.replace(/\s+/g, '_').toLowerCase()}`) || item.value}
            </span>
          );
        })}
      </div>
    );
  };

  // Show badges when hidden
  if (!showRollCategories) {
    return renderBadges();
  }

  return (
    <div className="space-y-3 mt-3">
      {/* Show badges even when categories are open */}
      {renderBadges()}
      {(Object.keys(ROLL_CATEGORIES) as Array<keyof typeof ROLL_CATEGORIES>).map((categoryKey) => {
        const category = ROLL_CATEGORIES[categoryKey];
        const selectedValue = rollCategories[categoryKey] || '';
        
        return (
          <Collapsible
            key={categoryKey}
            open={isCategoryOpen(categoryKey)}
            onOpenChange={() => toggleCategory(categoryKey)}
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-left text-black bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
              <span>{t(`calendar.roll_category.${categoryKey}`) || category.label}</span>
              {isCategoryOpen(categoryKey) ? (
                <ChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <div className="flex flex-wrap gap-1">
                {category.values.map((value) => {
                  const isSelected = selectedValue === value.key;
                  const colorClass = isSelected
                    ? value.isPositive
                      ? 'bg-green-500 text-white border-green-500'
                      : value.isNegative
                      ? 'bg-red-500 text-white border-red-500'
                      : 'bg-black text-white border-black'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50';
                  
                  return (
                    <button
                      key={value.key}
                      type="button"
                      onClick={() => handleValueSelect(categoryKey, value.key)}
                      className={`px-2 py-1 text-xs font-medium rounded border transition-colors duration-200 ${colorClass}`}
                    >
                      {t(`calendar.roll_value.${value.key.replace(/\s+/g, '_').toLowerCase()}`) || value.key}
                    </button>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
};

